import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { globalState } from '../store/globalStore';
import type { HistoryDateRangePersisted } from '../store/globalStore';
import {
  EXTRACT_LIMIT_DAYS,
  MEASUREMENTS_HISTORY_DEFAULT_RANGE_DAYS,
} from './utils';

export function getDefaultHistoryDateRange(): { from: Dayjs; to: Dayjs } {
  return {
    from: dayjs().subtract(MEASUREMENTS_HISTORY_DEFAULT_RANGE_DAYS, 'day').startOf('day'),
    to: dayjs().endOf('day'),
  };
}

function isValidStoredRange(from: Dayjs, to: Dayjs): boolean {
  if (!from.isValid() || !to.isValid()) return false;
  if (from.isAfter(to, 'day')) return false;
  const span = to.startOf('day').diff(from.startOf('day'), 'day') + 1;
  if (span > EXTRACT_LIMIT_DAYS) return false;
  const endToday = dayjs().endOf('day');
  if (to.isAfter(endToday)) return false;
  if (from.isAfter(endToday)) return false;
  return true;
}

function parsePersisted(raw: HistoryDateRangePersisted | undefined): {
  from: Dayjs;
  to: Dayjs;
} | null {
  if (!raw?.from || !raw?.to) return null;
  const from = dayjs(raw.from);
  const to = dayjs(raw.to);
  if (!isValidStoredRange(from, to)) return null;
  return {
    from: from.startOf('day'),
    to: to.endOf('day'),
  };
}

/** Returns validated range from global store, or null if missing / invalid. */
export function getHistoryDateRangeFromStore(
  moduleId: string | null | undefined,
): {
  from: Dayjs;
  to: Dayjs;
} | null {
  if (moduleId == null || moduleId === '') return null;
  const raw = globalState.historyDateRangeByModuleId[moduleId];
  return parsePersisted(raw);
}

/** Persists range for the module; pass null to clear. */
/** `YYYY-MM-DD_YYYY-MM-DD` for export filenames; null if range is missing or invalid. */
export function formatHistoryDateRangeForFilename(
  from: Dayjs | null | undefined,
  to: Dayjs | null | undefined,
): string | null {
  if (from == null || to == null) return null;
  if (!from.isValid() || !to.isValid()) return null;
  return `${from.format('YYYY-MM-DD')}_${to.format('YYYY-MM-DD')}`;
}

export function setHistoryDateRangeInStore(
  moduleId: string | null | undefined,
  from: Dayjs | null,
  to: Dayjs | null,
): void {
  if (moduleId == null || moduleId === '') return;
  if (!from || !to) {
    delete globalState.historyDateRangeByModuleId[moduleId];
    return;
  }
  globalState.historyDateRangeByModuleId[moduleId] = {
    from: from.toISOString(),
    to: to.toISOString(),
  };
}
